import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { DashboardWorksiteDistribution } from '../dashboardTypes'
import {
  COMPLIANT_DOT_COLOR,
  GeographicView,
  geographicSitePath,
  NONCOMPLIANT_DOT_COLOR,
  OFFICE_DOT_COLOR,
  worksiteFeatureCollection,
} from './worksiteDistribution'
import WorksiteDistributionList from './WorksiteDistributionList'
import WorksiteDistributionMap from './WorksiteDistributionMap'

const MapFrame = styled.div`
  position: relative;
  height: 280px;
  margin: 0.25rem 1.5rem 0;
  border-radius: 0.75rem;
  overflow: hidden;
  background: ${(props) => props.theme.palette.grey.grey100};
`

const Legend = styled.div`
  position: absolute;
  left: 0.75rem;
  bottom: 0.75rem;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: ${(props) => props.theme.palette.white};
  box-shadow: ${(props) => props.theme.dropShadows.normal};
`

const LegendDot = styled.span<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${(props) => props.$color};
`

const Toggle = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem;
  border-radius: 999px;
  background-color: ${palette.grey.grey200};
  flex-shrink: 0;
`

const ToggleOption = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.25;
  color: ${(props) =>
    props.$active ? palette.text.primary : palette.text.secondary};
  background-color: ${(props) => (props.$active ? palette.white : 'transparent')};
  box-shadow: ${(props) =>
    props.$active ? '0 1px 2px rgba(13, 12, 12, 0.08)' : 'none'};
`

export const GeographicViewToggle = ({
  value,
  onChange,
}: {
  value: GeographicView
  onChange: (view: GeographicView) => void
}) => (
  <Toggle role='group' aria-label='Geographic view'>
    <ToggleOption
      type='button'
      $active={value === 'offices'}
      aria-pressed={value === 'offices'}
      onClick={() => onChange('offices')}
    >
      Offices
    </ToggleOption>
    <ToggleOption
      type='button'
      $active={value === 'compliance'}
      aria-pressed={value === 'compliance'}
      onClick={() => onChange('compliance')}
    >
      Compliance
    </ToggleOption>
  </Toggle>
)

interface GeographicDistributionProps {
  sites: DashboardWorksiteDistribution[]
  view: GeographicView
}

const GeographicDistribution = ({
  sites,
  view,
}: GeographicDistributionProps) => {
  const navigate = useNavigate()
  const geojson = useMemo(
    () => worksiteFeatureCollection(sites, view),
    [sites, view]
  )
  const openSite = useCallback(
    (locationKey: string) => {
      const path = geographicSitePath(view, locationKey)
      if (path) {
        navigate(path)
      }
    },
    [navigate, view]
  )

  return (
    <div>
      <MapFrame>
        <WorksiteDistributionMap geojson={geojson} onSiteClick={openSite} />
        <Legend>
          {view === 'compliance' ? (
            <>
              <LegendDot $color={COMPLIANT_DOT_COLOR} />
              <Text variant='caption'>Compliant</Text>
              <LegendDot $color={NONCOMPLIANT_DOT_COLOR} />
              <Text variant='caption'>Non-compliant</Text>
            </>
          ) : (
            <>
              <LegendDot $color={OFFICE_DOT_COLOR} />
              <Text variant='caption'>Pin size = people at site</Text>
            </>
          )}
        </Legend>
      </MapFrame>
      <WorksiteDistributionList
        sites={sites}
        view={view}
        onSiteClick={openSite}
      />
    </div>
  )
}

export default React.memo(GeographicDistribution)
